import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useCan } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import { useRentLabel } from '../LeaseVerdictBadge'
import { Button } from '../ui/button'

/**
 * The draft leases an import read (a rent roll, a lease PDF), listed before
 * the dashboard's approve-all opens them (spec 2026-10-25-03 §5). Each links
 * to its lease page, where it can be corrected first. Hidden without
 * `contracts.read`, and when the import holds no draft lease.
 */
export function ImportDraftLeasesReview({ org, importUid }: { org: string; importUid: string }) {
    const { t } = useTranslation('leases')
    const { formatDate } = useFormatters()
    const rentLabel = useRentLabel()
    const canRead = useCan('contracts.read')
    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/leases',
        { params: { path: { orgCode: org }, query: { import_uid: importUid, status: 'draft', limit: 100, include: 'values_to_verify' } } },
        { enabled: canRead },
    )
    const drafts = data?.data ?? []
    if (!canRead || drafts.length === 0) return null
    const date = (d?: string | null) => (d ? formatDate(d) : '—')
    // The values to verify first, per lease and in all, so the review time is
    // known before it starts (spec 2026-10-27-12 §E).
    const toVerify = drafts.reduce((n, l) => n + (l.values_to_verify ?? 0), 0)

    return (
        <section className="rounded-lg border bg-card p-4 space-y-3" data-testid="import-draft-leases">
            <div>
                <h2 className="font-medium text-foreground">{t('import_review.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('import_review.description')}</p>
                <p className={`text-sm ${toVerify > 0 ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="import-leases-to-verify" data-count={toVerify}>
                    {t('import_review.to_verify_total', { count: toVerify })}
                </p>
            </div>
            <ul className="divide-y">
                {drafts.map((lease) => (
                    <li key={lease.uid} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm" data-testid="import-draft-lease-row">
                        <div className="min-w-0">
                            <div className="font-medium">{lease.tenants.join(', ') || t('no_tenant')}</div>
                            <div className="text-muted-foreground">
                                {lease.lots.map((l) => l.name).join(', ') || '—'} · {rentLabel(lease.rent_amount_minor, lease.rent_currency, lease.rent_frequency)} ·{' '}
                                {date(lease.start_date)} → {date(lease.end_date)}
                            </div>
                            {(lease.values_to_verify ?? 0) > 0 && (
                                <div className="text-destructive" data-testid="import-lease-to-verify">
                                    {t('import_review.to_verify', { count: lease.values_to_verify })}
                                </div>
                            )}
                        </div>
                        <Button asChild variant="outline" size="sm">
                            <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: lease.uid }} data-testid="import-draft-lease-link">
                                {t('import_review.open')}
                            </Link>
                        </Button>
                    </li>
                ))}
            </ul>
        </section>
    )
}
